import { useContext } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useRecoilValue } from 'recoil';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { chatIframeRef } from '../../atoms/shared';
import * as api from '../../services/api';
import useAccount from '../account/useAccount';
import useConfetti from '../useConfetti';
import useToaster from '../useToaster';

const useEmployee = ({ employeeId, enabled = true } = {}) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const chatIframeRefValue = useRecoilValue(chatIframeRef);

  const storeKey = ['employee', accountId, employeeId];
  const { isLoading, data, refetch } = useQuery(storeKey, () => api.fetchEmployee({ accountId, employeeId }), {
    staleTime: 0,
    cacheTime: 0,
    enabled: enabled && !!employeeId
  });

  const { addSuccess, addError } = useToaster();
  const { setFire } = useConfetti();

  const { mutateAsync: updateEmployee, isLoading: isLoadingUpdateEmployee } = useMutation(
    (values) =>
      employeeId ? api.updateEmployee({ accountId, employeeId, ...values }) : api.addEmployee({ accountId, ...values }),
    {
      throwOnError: true,
      onSuccess: (_, { invalidateEmployee }) => {
        chatIframeRefValue?.current?.contentWindow?.postMessage?.({ event: 'newEmployee' }, '*');
        queryClient.invalidateQueries('employees');
        queryClient.invalidateQueries(storeKey);
        queryClient.invalidateQueries('employeesStats');
        addSuccess(employeeId ? t('successUpdateEmployee') : t('successAddEmployee'));
        invalidateEmployee && queryClient.invalidateQueries(storeKey, { exact: true });
      }
    }
  );

  const { mutateAsync: deleteEmployee } = useMutation(
    ({ fields }) => api.updateEmployee({ accountId, employeeId, isDeleted: true, fields }),
    {
      onSuccess: (_, { onSuccess }) => {
        queryClient.invalidateQueries('employees');
        queryClient.invalidateQueries('employeesStats');
        onSuccess();
      }
    }
  );

  const { mutateAsync: permanentlyDeleteEmployee } = useMutation(
    () => api.permanentlyDeleteEmployee({ accountId, employeeId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employees');
        queryClient.invalidateQueries('employeesStats');
      }
    }
  );

  const { mutateAsync: inviteEmployee } = useMutation(() => api.inviteEmployee({ accountId, employeeId }), {
    onSuccess: () => {
      return addSuccess(t('invitationSentSuccess'));
    }
  });

  const { mutateAsync: addBulkEmployees } = useMutation(
    ({ employees }) => api.updateBulkEmployees({ accountId, employees }),
    {
      onSettled: () => {
        chatIframeRefValue?.current?.contentWindow?.postMessage?.({ event: 'newEmployees' }, '*');
      },
      onSuccess: () => {
        addSuccess(t('addBulkEmployeesSuccess'));
        chatIframeRefValue?.current?.contentWindow?.postMessage?.({ event: 'newEmployee' }, '*');
        const { data: employeeData } = queryClient.getQueryData(['employeesStats', accountId], { exact: false }) || {};
        if (employeeData) {
          const isFirst = Object.values(employeeData).every((value) => value === 0);
          isFirst && setFire(true);
        }
        queryClient.invalidateQueries('employees');
        queryClient.invalidateQueries(['dashboard', 'account', accountId], { exact: true });
        queryClient.invalidateQueries(['dashboard', 'employees']);
        queryClient.invalidateQueries('employeesStats');
        queryClient.invalidateQueries('positions');
        queryClient.invalidateQueries('fields');
      },
      onError: () => {
        addError(t('addBulkEmployeesError'));
      }
    }
  );

  // This request only queues a background job. Its outcome arrives over the socket, so the toast, the
  // confetti and the invalidations all live on the completion path in useEmployeesImportSocket.
  const { mutateAsync: importEmployees } = useMutation(
    async ({ uploadKey, branches, fields }) => {
      const { data: importJob } = await api.importEmployees({ accountId, uploadKey, branches, fields });
      return importJob?.jobId;
    },
    {
      onError: () => {
        addError(t('addBulkEmployeesError'));
      }
    }
  );

  const { mutateAsync: addBulkEmployeesJobs } = useMutation(
    ({ jobEntries }) => api.createBulkEmployeesJobs({ accountId, jobEntries }),
    {
      onSuccess: () => {
        addSuccess(t('addBulkEmployeesJobsSuccess'));
      },
      onError: () => {
        addError(t('addBulkEmployeesJobsError'));
      }
    }
  );

  const { mutateAsync: resendAdminEmail } = useMutation(() => api.resendAdminEmail({ accountId, employeeId }));

  const { mutateAsync: updateEmployeePhoto } = useMutation(
    ({ employeePhotoId, isSharedWithClient }) =>
      api.updateEmployeePhoto({ accountId, employeeId, employeePhotoId, isSharedWithClient }),
    {
      onSuccess: (_, { employeePhotoId, isSharedWithClient }) => {
        const previousData = queryClient.getQueryData(storeKey) || {};

        const newData = {
          data: {
            ...previousData?.data,
            photos: previousData?.data?.photos?.map((photo) =>
              photo.employeePhotoId === employeePhotoId
                ? {
                    ...photo,
                    isSharedWithClient
                  }
                : photo
            )
          }
        };

        queryClient.setQueryData(storeKey, newData);
      }
    }
  );

  return {
    isLoading: isLoading && employeeId,
    data: data?.data,
    updateEmployee,
    inviteEmployee,
    deleteEmployee,
    permanentlyDeleteEmployee,
    addBulkEmployees,
    importEmployees,
    addBulkEmployeesJobs,
    refetch,
    resendAdminEmail,
    updateEmployeePhoto,
    isLoadingUpdateEmployee
  };
};

export default useEmployee;
